import { useEffect, useState } from "react";
import { HoverPreview } from "./HoverPreview";
import { Reprice, WithReason } from "./Sales";
import type { SaleSummary } from "./api";
import { PageHeader } from "./layout/Layout";
import { hashParam } from "./layout/router";
import { type Account, ApiError, type MoneyLines, api, formatMoney } from "./api";
import { SelectField } from "./fields";
import { Alert, Box, Empty, Loading, Pager, Table, Th, TwoLine } from "./ui";

/**
 * Every account's money in and out together, the OCBC and Wise statements and the cash
 * drawers, for the accounts the company files (D, 10 October 2026: PayNow and cash both have
 * to be shown). Bank reconciliation is where OCBC is matched; this is the whole picture.
 */
export function MoneyLedger({ startAccount = "" }: { readonly startAccount?: string } = {}): React.ReactElement {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [account, setAccount] = useState(startAccount);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [direction, setDirection] = useState<"" | "in" | "out">("");
  const [page, setPage] = useState(1);
  const [lines, setLines] = useState<MoneyLines | undefined>();
  const [error, setError] = useState<string | undefined>();
  /** A sale's amount being put right from here (D, 29 October 2026: "under company cash sales"). */
  const [repricing, setRepricing] = useState<SaleSummary | undefined>();
  /** A sale keyed in twice or never made, taken off from here (D, 4 November 2026: "I keyed in twice by mistake"). */
  const [removing, setRemoving] = useState<SaleSummary | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [again, setAgain] = useState(0);

  useEffect(() => {
    void api.accounts().then((result) => setAccounts(result.accounts.filter((one) => one.isActive && (one.kind === "bank" || one.kind === "cash"))));
  }, []);
  const filter = { account, from, to, direction };
  useEffect(() => {
    api
      .moneyLines({ ...filter, page })
      .then((result) => {
        setLines(result);
        setError(undefined);
      })
      .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not load the money in and out"));
  }, [account, from, to, direction, page, again]);

  /** The sale behind a line, as the sales list has it, for putting it right from here. */
  function openSale(line: { day: string; saleId?: string | null }, then: (sale: SaleSummary) => void): void {
    void api
      .sales({ day: line.day })
      .then((result) => {
        const sale = result.sales.find((one) => one.id === line.saleId);
        if (!sale) return setError("That sale could not be found");
        if (sale.reconciliation === "matched") return setError(`${sale.reference} is matched to a bank line. Undo that match on Bank reconciliation first`);
        then(sale);
      })
      .catch((problem: unknown) => setError(problem instanceof ApiError ? problem.message : "Could not open the sale"));
  }

  const choose = <T,>(set: (value: T) => void) => (value: T): void => {
    set(value);
    setPage(1);
  };

  return (
    <Box
      title="All money in and out"
      count={lines?.total ?? 0}
      actions={
        <a className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0" href={api.moneyExportUrl(filter)}>
          <i className="ri-file-excel-2-line" /> Download Excel
        </a>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {notice ? <Alert tone="success" onClose={() => setNotice(undefined)}>{notice}</Alert> : null}
      {repricing ? (
        <Reprice
          sale={repricing}
          onClose={() => setRepricing(undefined)}
          onDone={async (message) => {
            setRepricing(undefined);
            setNotice(message);
            setAgain((count) => count + 1);
          }}
        />
      ) : null}
      {removing ? (
        <WithReason
          title={`Remove ${removing.reference}, keyed in wrong`}
          question={`For a sale keyed in twice, or one that never happened as keyed. It comes off ${removing.saleDate}: ${removing.status === "paid" ? `its ${formatMoney(removing.totalCents)} off that day's ${removing.paymentMethod === "cash" ? "cash" : "PayNow takings"} and ` : ""}its items back into inventory, as if it had never been keyed. The sale stays on the sales list as voided, with your reason. A sale the customer returned is reversed instead.`}
          label="Remove sale"
          onClose={() => setRemoving(undefined)}
          onDone={async (reason) => {
            await api.removeSale(removing.id, reason);
            setRemoving(undefined);
            setNotice(`${removing.reference} removed from ${removing.saleDate}.`);
            setAgain((count) => count + 1);
          }}
        />
      ) : null}
      <div className="grid grid-cols-12 gap-4 mb-4">
        <div className="xl:col-span-4 sm:col-span-6 col-span-12">
          <SelectField
            id="money-account"
            label="Account"
            value={account || "all"}
            options={[
              { value: "all", label: "All: bank and cash" },
              { value: "bank", label: "All bank accounts" },
              { value: "cash", label: "All cash" },
              ...accounts.map((one) => ({ value: one.id, label: `${one.name} (${one.kind === "cash" ? "cash" : "bank"})` })),
            ]}
            onChange={choose((value: string) => setAccount(value === "all" ? "" : value))}
          />
        </div>
        <div className="xl:col-span-2 sm:col-span-3 col-span-6">
          <label htmlFor="money-from" className="form-label">From</label>
          <input id="money-from" type="date" className="form-control" value={from} onChange={(event) => choose(setFrom)(event.target.value)} />
        </div>
        <div className="xl:col-span-2 sm:col-span-3 col-span-6">
          <label htmlFor="money-to" className="form-label">To</label>
          <input id="money-to" type="date" className="form-control" value={to} onChange={(event) => choose(setTo)(event.target.value)} />
        </div>
        <div className="xl:col-span-4 sm:col-span-6 col-span-12">
          <SelectField<"all" | "in" | "out">
            id="money-direction"
            label="Money in or out"
            value={direction || "all"}
            options={[
              { value: "all", label: "Money in and out" },
              { value: "in", label: "Money in" },
              { value: "out", label: "Money out" },
            ]}
            onChange={choose((value: "all" | "in" | "out") => setDirection(value === "all" ? "" : value))}
          />
        </div>
      </div>

      {!lines ? (
        <Loading />
      ) : lines.total === 0 ? (
        <Empty icon="ri-exchange-dollar-line">No money in or out for these filters.</Empty>
      ) : (
        <>
          <div className="flex flex-wrap gap-6 mb-3 text-[0.875rem]">
            <span>
              Money in <span className="font-semibold text-success">{formatMoney(lines.inCents)}</span>
            </span>
            <span>
              Money out <span className="font-semibold">{formatMoney(-lines.outCents)}</span>
            </span>
            <span>
              Net <span className="font-semibold">{formatMoney(lines.inCents + lines.outCents)}</span>
            </span>
          </div>
          <Table
            head={
              <>
                <Th>Date</Th>
                <Th>Payer, payee or description</Th>
                <Th end>Money in</Th>
                <Th end>Money out</Th>
                <Th>Account</Th>
                <Th>What it is</Th>
              </>
            }
          >
            {lines.lines.map((line) => (
              <tr key={`${line.source}-${line.id}`} className="border-b border-defaultborder">
                <td>{line.day}</td>
                <td className="!whitespace-normal min-w-[16rem]">
                  <TwoLine main={line.party || line.description} sub={line.party ? line.description : undefined} />
                </td>
                <td className="!text-end font-semibold text-success">{line.amountCents > 0 ? formatMoney(line.amountCents) : ""}</td>
                <td className="!text-end font-semibold">{line.amountCents < 0 ? formatMoney(-line.amountCents) : ""}</td>
                <td>
                  <TwoLine main={line.accountName} sub={line.source === "cash" ? "Cash" : "Bank"} />
                </td>
                <td className={`!whitespace-normal min-w-[14rem] ${line.what === "Not matched yet" ? "text-warning" : "text-[#8c9097] dark:text-white/50"}`}>
                  {line.what}
                  {line.saleId ? (
                    <>
                      <button
                        type="button"
                        className="ms-2 text-primary text-[0.75rem]"
                        title="Keyed at the wrong amount? Change it"
                        onClick={() => openSale(line, setRepricing)}
                      >
                        <i className="ri-pencil-line" /> Change the amount
                      </button>
                      <button
                        type="button"
                        className="ms-2 text-danger text-[0.75rem]"
                        title="Keyed in twice, or never happened? Take it off"
                        onClick={() => openSale(line, setRemoving)}
                      >
                        <i className="ri-delete-bin-line" /> Remove
                      </button>
                    </>
                  ) : null}
                  {line.category ? <span className="block text-defaulttextcolor dark:text-white/80 text-[0.75rem] font-semibold mt-0.5">{line.category}</span> : null}
                  {/* What it was for, and its photos (D, 15 October 2026: cash above all, which the government asks about). */}
                  {line.document ? (
                    <>
                      <span className="block text-defaulttextcolor dark:text-white/80 text-[0.75rem] mt-1">
                        <span className="font-semibold">{line.document.number}</span>
                        {line.document.items.slice(0, 6).map((item, index) => (
                          <span key={index} className="block">
                            {item.replace(/ \| Unit: SGD [^|]+ \| Total: SGD (.+)$/, " , $1").replace(/ \| Qty: ([^|]+) ,/, " × $1,")}
                          </span>
                        ))}
                        {line.document.items.length > 6 ? <span className="block text-[#8c9097]">and {line.document.items.length - 6} more</span> : null}
                        {line.document.discountCents ? <span className="block text-warning">Discount on the sale -{formatMoney(line.document.discountCents)}</span> : null}
                        {line.document.surchargeCents ? <span className="block">Added on the sale +{formatMoney(line.document.surchargeCents)}</span> : null}
                      </span>
                      {line.document.photoIds.length || line.document.pdfIds?.length ? (
                        <span className="flex flex-wrap gap-1 mt-1">
                          {line.document.pdfIds?.map((id) => (
                            <HoverPreview key={id} src={`/api/photos/${id}`} kind="pdf">
                              <a href={`/api/photos/${id}`} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-sm border border-defaultborder flex flex-col items-center justify-center text-danger text-[0.625rem] font-semibold">
                                <i className="ri-file-pdf-2-line text-[1rem]" />
                                PDF
                              </a>
                            </HoverPreview>
                          ))}
                          {line.document.photoIds.map((id) => (
                            <HoverPreview key={id} src={`/api/photos/${id}?preview=1`} kind="image">
                              <img src={`/api/photos/${id}?preview=1`} alt="" className="w-10 h-10 object-cover rounded-sm border border-defaultborder" />
                            </HoverPreview>
                          ))}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
          <Pager total={lines.total} page={lines.page} pages={lines.pages} onPage={setPage} pageSize={50} />
        </>
      )}
    </Box>
  );
}

/**
 * One account's money, or all of it (D, 15 October 2026: "Company accounts" and "All company
 * accounts and cash" in the menu), with every line showing what it was.
 */
export function MoneyPage(): React.ReactElement {
  const account = hashParam("account");
  const [name, setName] = useState<string | undefined>();
  useEffect(() => {
    if (!account) return;
    void api.accounts().then((result) => setName(result.accounts.find((one) => one.id === account)?.name));
  }, [account]);
  return (
    <>
      <PageHeader
        title={account ? (name ?? "Account") : "All company accounts and cash"}
        subtitle="Every movement of money in and out, bank and cash, with what each was for."
      />
      <MoneyLedger startAccount={account} />
    </>
  );
}
